import { useCallback } from "react";
import { useSnackbar } from "notistack";
// LENDMARQ DIVERGENCE: this app's own file hook, used directly — tpo has no
// equivalent document-streaming route, so there's no bridge in src/tpo.
import { useLoanFilesHook } from "../../../hooks/useLoanFilesHook";
import { submissionDocuments } from "./submissionHelpers";

/**
 * Download one file a borrower attached to their submission — assigned to a loan
 * or not.
 *
 * No endpoint on /borrower_submissions exposes a submission's documents: they
 * live in `application_documents` keyed by the submission oid, and what the
 * submission returns is `uploadMetadata` (display name, docGroup, docType) plus
 * `documents` (bare oids). Neither carries `file_storage_name`, which is what
 * /files/file_by_id needs to stream a blob.
 *
 * It is nonetheless DERIVABLE, because three pieces of our own code line up:
 *
 *   1. `buildUploadFormData` (useAxiosHook) appends one `uploadFiles` part per
 *      `uploadMetadata` entry, iterating that one array, and ships the same
 *      array as the submission's `uploadMetadata`. Parts and metadata are
 *      therefore parallel by construction, not by luck.
 *   2. `attach_uploads` iterates those parts in order and pushes each generated
 *      oid onto `doc_oids`, which becomes the submission's `documents`. So
 *      `documents[i]` belongs to `uploadMetadata[i]`.
 *   3. `upload_document` names the blob `{oid}{suffix}`, where suffix is the
 *      extension off the uploaded filename — and for the public forms that
 *      filename IS `file_display_name` (`usePublicFilenames`), which we have.
 *
 * So `file_storage_name = documents[i] + ext(uploadMetadata[i])`, streamed from
 * the submission's own container. That works the moment a borrower submits.
 *
 * The lengths are checked before trusting the pairing, and a lookup through
 * /files/loan_documents remains as a fallback for records that don't fit the
 * shape above — legacy submissions whose metadata lives in loan_documents, and
 * assigned ones, whose files carry_documents_to_loan has copied to the loan.
 */
const norm = (s) => String(s || "").trim().toLowerCase();

// Mirrors upload_document's suffix logic: a clean 1-5 char alphanumeric
// extension off the filename, else one guessed from the content type, else the
// backend's own `.bin` default.
const CONTENT_TYPE_EXT = {
  "application/pdf": ".pdf",
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/heic": ".heic",
  "image/webp": ".webp",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
  "application/vnd.ms-excel": ".xls",
  "text/csv": ".csv",
};

const storageSuffix = (file) => {
  const raw = file?.file_display_name || file?.name || "";
  const dot = raw.lastIndexOf(".");
  const ext = dot > -1 ? raw.slice(dot + 1) : "";
  if (ext && ext.length <= 5 && /^[a-z0-9]+$/i.test(ext)) {
    return `.${ext.toLowerCase()}`;
  }
  return CONTENT_TYPE_EXT[norm(file?.content_type)] || ".bin";
};

/**
 * The blob name for `file`, derived from the submission's parallel `documents`
 * and `uploadMetadata` arrays. Null when the arrays don't correspond — better to
 * fall through to a lookup than to hand back a confident wrong file.
 */
const derivedStorageName = (file, submission) => {
  const meta = submissionDocuments(submission);
  const oids = Array.isArray(submission?.documents) ? submission.documents : [];
  if (!meta.length || meta.length !== oids.length) return null;

  const i = meta.indexOf(file);
  const index =
    i > -1
      ? i
      : meta.findIndex(
          (m) =>
            norm(m.file_display_name || m.name) ===
            norm(file?.file_display_name || file?.name),
        );
  if (index < 0) return null;

  const oid = oids[index];
  // Only ever an id string here; anything else means the shape changed.
  if (typeof oid !== "string" || !oid) return null;
  return `${oid}${storageSuffix(file)}`;
};

export const useSubmissionFileDownload = () => {
  const { getLoanDocuments, getFileById } = useLoanFilesHook();
  const { enqueueSnackbar } = useSnackbar();

  const listDocuments = useCallback(
    (containerId) =>
      new Promise((resolve) => {
        if (!containerId) return resolve([]);
        getLoanDocuments({
          containerId,
          onSuccessFn: (d) => resolve(d?.loanDocuments || []),
          onFailFn: () => resolve([]),
        });
      }),
    [getLoanDocuments],
  );

  /** `file` is an entry from submissionDocuments(submission). */
  return useCallback(
    async (file, submission) => {
      const container = submission?.containerName || submission?._id;
      const wanted = norm(file?.file_display_name || file?.name);

      // 1. Already resolved (a loan_documents-shaped entry).
      let hit = file?.file_storage_name
        ? { storage: file.file_storage_name, container: file.containerName || container }
        : null;

      // 2. Derived from the submission itself — works unassigned.
      if (!hit) {
        const storage = derivedStorageName(file, submission);
        if (storage) hit = { storage, container };
      }

      // 3. Looked up, for records that don't fit that shape.
      if (!hit) {
        for (const containerId of [container, submission?.dealId].filter(
          Boolean,
        )) {
          const docs = await listDocuments(containerId);
          const match = docs.find(
            (d) =>
              norm(d.file_display_name) === wanted ||
              norm(d.file_name_as_loaded) === wanted,
          );
          if (match?.file_storage_name) {
            hit = {
              storage: match.file_storage_name,
              container: match.containerName || containerId,
            };
            break;
          }
        }
      }

      if (!hit?.container) {
        enqueueSnackbar("That file's storage record couldn't be resolved.", {
          variant: "warning",
        });
        return;
      }

      await getFileById({
        loanId: hit.container,
        file_storage_name: hit.storage,
        fileDownload: true,
        // The submission's container has no loan_documents row, so the backend
        // can't supply a display name — name the download here or it saves as
        // the raw blob name.
        fileDownloadName: file?.file_display_name || file?.name,
      });
    },
    [listDocuments, getFileById, enqueueSnackbar],
  );
};

export default useSubmissionFileDownload;
